/**
 * Card for one Beta post draft. Status-driven layout:
 *
 *   awaiting-image  → big "drop image / paste URL" zone + promptHint
 *   rendering       → spinner over the slides
 *   ready-for-review → 4-slide swiper preview + actions
 *
 * Drag/drop or paste a Higgsfield URL to attach.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useIsMobile } from "../lib/useIsMobile";
import { CalendarClock, Check, ChevronLeft, ChevronRight, ClipboardCopy, Copy, History, ImagePlus, Loader2, MessageSquarePlus, Minus, Pencil, Plus, RefreshCw, Repeat, RotateCcw, Save, Send, Sparkles, Trash2, X } from "lucide-react";
import { BrandDateTimePicker } from "./BrandDateTimePicker";
import { ConfirmDialog } from "./ConfirmDialog";
import { DestinationToggle, DestinationEmpty } from "./DestinationToggle";
import { RescheduleEditor } from "./RescheduleEditor";
import {
  attachBetaImage,
  generateBetaImage,
  inspirationImageUrl,
  listInspiration,
  type InspirationEntry,
  betaSlotUrl,
  discardBetaDraft,
  duplicateBetaDraft,
  getBetaCaptionTemplate,
  listBetaPostVersions,
  betaPostVersionCoverUrl,
  restoreBetaPostVersion,
  listSchedulingChannels,
  patchBetaDraft,
  publishBetaDraft,
  rescheduleBetaDraft,
  regenerateBetaPrompt,
  regenerateBetaText,
  adjustBetaQuoteLength,
  rerenderBetaSlides,
  type BetaPostDraft,
  type BetaPostVersionSummary,
  type BetaPublishResult,
  type SchedulingChannel,
} from "../api";

interface Props {
  draft: BetaPostDraft;
  onChange: () => void;
  /** Server flag (QL_POST_LENGTH_CONTROL). Off ⇒ no Shorter/Longer buttons,
   *  so a disabled route can never surface as a broken button. */
  lengthControl?: boolean;
  /** "Schedule again" (2026-09-14): the server made a fresh ready COPY of
   *  this published post — hand it to the page so it can show it and open
   *  its Publish modal. Absent ⇒ the button is hidden. */
  onRecycled?: (copy: BetaPostDraft) => void;
  /** Open the Publish modal on mount — set by the page on the card of a
   *  just-recycled copy, so "Schedule again" lands straight in the scheduler. */
  autoOpenPublish?: boolean;
}

/** Which single card action is in flight. Named (not inlined) because the
 *  EditModal takes the same value — one definition keeps them from drifting. */
type CardBusy =
  | "attach" | "generate" | "regen" | "regenPrompt" | "rerender"
  | "discard" | "saveEdit" | "shorter" | "longer" | "recycle" | null;

const SLOTS = ["01-cover-ar", "02-cover-en", "03-body-ar", "04-body-en"] as const;
type Slot = (typeof SLOTS)[number];

// Services that can receive an image carousel, in display order. Curated to
// image-capable networks — matches the backend service groups in
// postDraftsBetaRoutes.ts. (The VIDEO schedule modal lists every connected
// Buffer channel; a carousel only goes to these.) Adding a future
// image-capable platform = one entry here + its backend group.
const CAROUSEL_SERVICES: SchedulingChannel["service"][] = [
  "instagram",
  "twitter",
  "pinterest",
  "linkedin",
];

const SLOT_LABEL: Record<Slot, string> = {
  "01-cover-ar": "Arabic Quote",
  "02-cover-en": "English Quote",
  "03-body-ar": "Arabic Reflection",
  "04-body-en": "English Reflection",
};

export function BetaPostCard({ draft, onChange, lengthControl = false, onRecycled, autoOpenPublish = false }: Props): React.JSX.Element {
  const [busy, setBusy] = useState<CardBusy>(null);
  // A direction the server has told us this quote can't move further. Learned
  // from its 409 rather than by duplicating the length bands client-side.
  const [blockedLength, setBlockedLength] = useState<{ shorter?: string; longer?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [reschedOpen, setReschedOpen] = useState(false);
  const isScheduled = draft.status === "scheduled";
  const isPublished = draft.status === "published";
  // "Regenerate with a note" — a collapsible textarea whose contents are
  // injected into the generation prompt for a steered re-roll.
  const [regenNoteOpen, setRegenNoteOpen] = useState(false);
  const [regenNote, setRegenNote] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [slideIdx, setSlideIdx] = useState(0);
  // Cache-bust slide PNGs after a *live* re-render in this session.
  // Initial value is stable across mounts so the browser HTTP cache
  // (ETag + max-age on /api/post-drafts-beta/:id/:slot, see
  // src/server/postDraftsBetaRoutes.ts) actually hits on page revisits.
  // The setCacheKey(Date.now()) calls below kick in when the operator
  // regenerates/rerenders, forcing a fresh fetch within the session.
  const [cacheKey, setCacheKey] = useState(0);
  // Background quote-length actions (2026-08-19) finish AFTER the POST
  // returns — the fresh slides arrive via polling. Watch the draft's save
  // stamp and bust the image cache whenever it moves, whoever moved it.
  const saveStamp = draft.generatedAt;
  const prevStampRef = useRef(saveStamp);
  useEffect(() => {
    if (prevStampRef.current !== saveStamp) {
      prevStampRef.current = saveStamp;
      setCacheKey(Date.now());
      setBlockedLength({});
    }
  }, [saveStamp]);
  // Server-persisted action marker — unlike local `busy`, survives
  // refresh/navigation because it lives on the draft itself. While set, every
  // mutating control on this card is disabled (operator rule 2026-08-19: a
  // card being rewritten must not be publishable).
  const serverBusy = draft.busy ?? null;
  const anyBusy = busy !== null || serverBusy !== null;
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<BetaPostVersionSummary[] | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copying" | "copied" | "error">("idle");
  const [editOpen, setEditOpen] = useState(false);
  const [genModalOpen, setGenModalOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  // When the operator clicks "Regenerate image" on a ready-for-review card,
  // we swap the slide preview area for the same ImageDropZone the awaiting-
  // image state uses — so they can read the current promptHint, regenerate
  // it (or paste a URL), and only then fire the actual image gen. This
  // matches the user expectation of "step back to the prompt before
  // burning another GPT Image call". Cleared automatically once a fresh
  // generation kicks off (the rendering-overlay branch takes over).
  const [showPromptPanel, setShowPromptPanel] = useState(false);
  const currentVersion = draft.version ?? 1;
  const [restoreUndo, setRestoreUndo] = useState<{ from: number } | null>(null);
  const [restoring, setRestoring] = useState<number | null>(null);
  const handleRestore = async (version: number): Promise<void> => {
    if (restoring !== null) return;
    const from = currentVersion; // the version we're leaving — the restore archives it, so Undo can roll forward
    setRestoring(version);
    try {
      await restoreBetaPostVersion(draft.id, version);
      onChange();
      setRestoreUndo({ from });
      window.setTimeout(() => setRestoreUndo((u) => (u && u.from === from ? null : u)), 6000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRestoring(null);
    }
  };

  // Editable draft state — initialized from the live draft, reset on cancel
  // or when the underlying draft id/version changes. Font-size overrides
  // are deliberately NOT in this state: they're nudged via stepper
  // buttons that PATCH immediately, so the form's Cancel button doesn't
  // need to roll them back.
  const initialEdit = useMemo(() => ({
    quote: draft.content.quote,
    arabicQuote: draft.content.arabicQuote,
    arabic: draft.content.arabic,
    english: draft.content.english,
    promptHint: draft.content.promptHint,
    vibe: draft.content.vibe,
    attribution: draft.content.attribution ?? "",
  }), [draft.id, draft.version]);
  const [edit, setEdit] = useState(initialEdit);
  useEffect(() => { setEdit(initialEdit); }, [initialEdit]);

  useEffect(() => {
    if (!historyOpen) return;
    let cancelled = false;
    void listBetaPostVersions(draft.id)
      .then((rows) => { if (!cancelled) setVersions(rows); })
      .catch(() => { if (!cancelled) setVersions([]); });
    return () => { cancelled = true; };
  }, [historyOpen, draft.id, draft.version]);

  // ESC key closes the edit modal — only when the modal is open so we
  // don't intercept globally. Also locks body scroll while open so the
  // backdrop feels modal-ish on long pages.
  useEffect(() => {
    if (!editOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && busy !== "saveEdit") {
        setEdit(initialEdit);
        setEditOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [editOpen, busy, initialEdit]);

  const handlePasteUrl = async (): Promise<void> => {
    const url = imageUrl.trim();
    if (!url) return;
    setBusy("attach");
    setError(null);
    try {
      await attachBetaImage(draft.id, { url });
      setImageUrl("");
      setCacheKey(Date.now());
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const handleOpenGenerateModal = (): void => {
    if (draft.status === "rendering" || busy === "generate") return;
    setGenModalOpen(true);
  };

  /** Fired by the GenerateModal once the operator has picked references. */
  const handleGenerateWithRefs = async (inspirationIds: string[]): Promise<void> => {
    if (draft.status === "rendering" || busy === "generate") return;
    setGenModalOpen(false);
    // Close the prompt panel — the rendering-overlay branch will take
    // over the preview area while GPT Image runs, and the slide preview
    // returns when status flips to ready-for-review.
    setShowPromptPanel(false);
    setBusy("generate");
    setError(null);
    try {
      await generateBetaImage(draft.id, { inspirationIds });
      setCacheKey(Date.now());
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const handleRegen = async (feedback?: string): Promise<void> => {
    setBusy("regen");
    setError(null);
    try {
      await regenerateBetaText(draft.id, feedback ? { feedback } : {});
      setRegenNote("");
      setRegenNoteOpen(false);
      setCacheKey(Date.now());
      // A fresh quote has its own room to grow or shrink.
      setBlockedLength({});
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Re-cut the hero quote ~15% shorter or longer. Reflections, image prompt
   * and the attached image are untouched; the prior version is archived
   * server-side, so Restore undoes it.
   */
  const handleLength = async (direction: "shorter" | "longer"): Promise<void> => {
    // 202 flow (2026-08-19): the rewrite + re-render run in the background;
    // progress shows from the server-persisted busy marker via polling, and
    // the outcome (incl. "Kept your quote — …") arrives as a persisted notice
    // on the draft. The catch only sees the synchronous 409s (band edge,
    // double-press).
    setBusy(direction);
    setError(null);
    try {
      await adjustBetaQuoteLength(draft.id, direction);
      onChange();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      if (/already as (short|long)/i.test(msg)) {
        setBlockedLength((b) => ({ ...b, [direction]: msg }));
      }
    } finally {
      setBusy(null);
    }
  };

  const handleRegenPrompt = async (): Promise<void> => {
    setBusy("regenPrompt");
    setError(null);
    try {
      await regenerateBetaPrompt(draft.id);
      setCacheKey(Date.now());
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const handleRerender = async (): Promise<void> => {
    setBusy("rerender");
    setError(null);
    try {
      await rerenderBetaSlides(draft.id);
      setCacheKey(Date.now());
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const handleDiscard = (): void => {
    setConfirmDiscardOpen(true);
  };

  const confirmDiscard = async (): Promise<void> => {
    setConfirmDiscardOpen(false);
    setBusy("discard");
    setError(null);
    try {
      await discardBetaDraft(draft.id);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  };

  const copyPromptHint = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(draft.content.promptHint);
    } catch { /* ignore */ }
  };

  /**
   * Copy the currently visible slide PNG to the clipboard so the
   * operator can paste it straight into Instagram, Buffer, etc.
   * Uses the Clipboard API's ClipboardItem (supported in modern
   * Chromium/Safari; the catch handler falls back to an error pulse).
   */
  /**
   * Nudge a cover slide's font-size by `delta` (px). One click = one
   * step = one immediate save + re-render. When the slide is currently
   * on auto-fit, the first nudge anchors at a sensible baseline (so
   * "+ Bigger" from auto doesn't reset to a random number).
   *
   * Step size is 8px — small enough for precise control, big enough
   * that the change reads on the slide. Negative delta shrinks.
   */
  const handleNudgeSize = async (slide: "ar" | "en", delta: number): Promise<void> => {
    // Baselines are close to where the auto-fit usually lands so the
    // first nudge from AUTO is a subtle tweak, not a jump. Step size
    // is set by the caller (currently ±4 px — small enough to dial in
    // precisely without overshooting in one click).
    const BASELINE_AR = 96;
    const BASELINE_EN = 88;
    const MIN_PX = 48;
    const MAX_PX = 180;
    const current = slide === "ar"
      ? (draft.slideOverrides?.coverArFontSize ?? 0)
      : (draft.slideOverrides?.coverEnFontSize ?? 0);
    const base = current > 0 ? current : (slide === "ar" ? BASELINE_AR : BASELINE_EN);
    const next = Math.max(MIN_PX, Math.min(MAX_PX, base + delta));
    setBusy("saveEdit");
    setError(null);
    try {
      const overrides: { coverArFontSize?: number; coverEnFontSize?: number } = {};
      if (slide === "ar") overrides.coverArFontSize = next;
      else overrides.coverEnFontSize = next;
      await patchBetaDraft(draft.id, { slideOverrides: overrides });
      setCacheKey(Date.now());
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const handleResetSize = async (slide: "ar" | "en"): Promise<void> => {
    setBusy("saveEdit");
    setError(null);
    try {
      const overrides: { coverArFontSize?: number | null; coverEnFontSize?: number | null } = {};
      if (slide === "ar") overrides.coverArFontSize = null;
      else overrides.coverEnFontSize = null;
      await patchBetaDraft(draft.id, { slideOverrides: overrides });
      setCacheKey(Date.now());
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const handleSaveEdit = async (): Promise<void> => {
    setBusy("saveEdit");
    setError(null);
    try {
      // Build a minimal content patch — font-size overrides are nudged
      // via dedicated stepper buttons and aren't part of the form state.
      const contentPatch: Partial<typeof draft.content> = {};
      if (edit.quote !== draft.content.quote) contentPatch.quote = edit.quote;
      if (edit.arabicQuote !== draft.content.arabicQuote) contentPatch.arabicQuote = edit.arabicQuote;
      if (edit.arabic !== draft.content.arabic) contentPatch.arabic = edit.arabic;
      if (edit.english !== draft.content.english) contentPatch.english = edit.english;
      if (edit.promptHint !== draft.content.promptHint) contentPatch.promptHint = edit.promptHint;
      if (edit.vibe !== draft.content.vibe) contentPatch.vibe = edit.vibe;
      if (edit.attribution !== (draft.content.attribution ?? "")) contentPatch.attribution = edit.attribution;
      if (Object.keys(contentPatch).length === 0) {
        // No textual change — close the panel without a redundant save.
        setEditOpen(false);
        return;
      }
      await patchBetaDraft(draft.id, { content: contentPatch });
      setCacheKey(Date.now());
      setEditOpen(false);
      onChange();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const handleCopyImage = async (): Promise<void> => {
    setCopyState("copying");
    try {
      const url = `${betaSlotUrl(draft.id, SLOTS[slideIdx])}?t=${cacheKey}`;
      const r = await fetch(url, { credentials: "same-origin" });
      if (!r.ok) throw new Error(`fetch failed: ${r.status}`);
      const blob = await r.blob();
      if (typeof ClipboardItem === "undefined") {
        throw new Error("ClipboardItem not supported in this browser");
      }
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }),
      ]);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 1800);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setCopyState("error");
      setTimeout(() => setCopyState("idle"), 2500);
    }
  };

  // A just-recycled copy opens its scheduler immediately (once, on mount).
  useEffect(() => {
    if (autoOpenPublish) setPublishOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only by design
  }, []);

  const handleRecycle = async (): Promise<void> => {
    if (!onRecycled) return;
    setBusy("recycle");
    setError(null);
    try {
      onRecycled(await duplicateBetaDraft(draft.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const hasImage = !!draft.imageFilename;
  const statusLabel: Record<BetaPostDraft["status"], string> = {
    "generating-text": "Writing…",
    "awaiting-image": "Awaiting image",
    rendering: "Rendering…",
    "ready-for-review": "Ready",
    scheduled: "Scheduled",
    published: "Published",
    discarded: "Discarded",
    "generation-failed": "Failed",
  };
  const statusColor: Record<BetaPostDraft["status"], string> = {
    "generating-text": "rgba(255,213,0,.85)",
    "awaiting-image": "rgba(255,213,0,.75)",
    rendering: "rgba(255,213,0,.85)",
    "ready-for-review": "rgba(74,222,128,.85)",
    scheduled: "rgba(74,222,128,.65)",
    published: "rgba(180,180,180,.65)",
    discarded: "rgba(255,255,255,.35)",
    "generation-failed": "rgba(248,113,113,.9)",
  };
  const isGeneratingText = draft.status === "generating-text";
  const isFailed = draft.status === "generation-failed";

  // While a background action holds the draft, the pill reports THAT, not the
  // stored status — parity with the Legacy card (operator rule 2026-08-19).
  const pillLabel = serverBusy ? "Re-cutting…" : statusLabel[draft.status];
  const pillColor = serverBusy ? "rgba(255,213,0,.85)" : statusColor[draft.status];

  return (
    <>
    <div
      style={{
        borderRadius: 18,
        background: "rgba(16,18,27,.72)",
        border: "1px solid rgba(255,255,255,.06)",
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ql-fg)" }}>{draft.theme}</div>
            {currentVersion > 1 && (
              <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                title={`Version ${currentVersion} — click to view history`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: historyOpen ? "rgba(255,213,0,.18)" : "rgba(255,213,0,.08)",
                  border: "1px solid rgba(255,213,0,.30)",
                  color: "var(--ql-yellow)",
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "var(--ql-font-mono)",
                  textTransform: "lowercase",
                  letterSpacing: ".04em",
                }}
              >
                <History size={10} strokeWidth={2.4} />
                v{currentVersion}
              </button>
            )}
          </div>
          <div style={{ fontSize: 11, color: "var(--ql-fg-muted)", fontFamily: "var(--ql-font-mono)" }}>
            generated {timeAgo(draft.generatedAt)} · vibe: {draft.content.vibe}
            {isScheduled && draft.publishedTo?.dueAt &&
              ` · for ${new Date(draft.publishedTo.dueAt).toLocaleString()}`}
            {draft.recycledFrom && (
              <span
                title={`Scheduled again from ${draft.recycledFrom}`}
                style={{ marginLeft: 8, color: "var(--ql-yellow)", fontWeight: 700 }}
              >
                · recycled
              </span>
            )}
          </div>
          {isScheduled && draft.publishedTo?.dueAt && (
            <button
              type="button"
              onClick={() => setReschedOpen((v) => !v)}
              title="Change the date & time"
              style={{
                alignSelf: "flex-start",
                marginTop: 2,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "3px 9px",
                borderRadius: 7,
                border: reschedOpen ? "1px solid rgba(255,213,0,.5)" : "1px solid rgba(255,255,255,.14)",
                background: reschedOpen ? "rgba(255,213,0,.10)" : "transparent",
                color: reschedOpen ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <CalendarClock size={11} strokeWidth={2} /> Edit time
            </button>
          )}
        </div>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            borderRadius: 999,
            background: "rgba(255,255,255,.04)",
            border: `1px solid ${pillColor}`,
            color: pillColor,
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: ".06em",
          }}
        >
          {serverBusy && <Loader2 size={11} className="ql-spin" />}
          {pillLabel}
        </span>
      </div>

      {isScheduled && reschedOpen && draft.publishedTo?.dueAt && (
        <div
          style={{
            padding: "12px 14px",
            borderRadius: 12,
            border: "1px solid rgba(255,213,0,.22)",
            background: "rgba(255,213,0,.04)",
            display: "grid",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ql-fg)" }}>Change date &amp; time</div>
          <RescheduleEditor
            currentISO={draft.publishedTo.dueAt}
            onSave={async (iso) => {
              const { errors } = await rescheduleBetaDraft(draft.id, iso);
              setReschedOpen(false);
              onChange();
              if (errors.length) {
                setError(
                  `Rescheduled, but ${errors.length} channel${errors.length === 1 ? "" : "s"} didn't move: ${errors
                    .map((e) => e.error)
                    .join("; ")}`,
                );
              }
            }}
            onCancel={() => setReschedOpen(false)}
            saveLabel="Save time"
            note={
              (draft.publishedTo.results?.filter((r) => r.postId && !r.error).length ?? 0) > 1
                ? `Moves all ${draft.publishedTo.results.filter((r) => r.postId && !r.error).length} channels of this post together.`
                : undefined
            }
          />
        </div>
      )}

      {historyOpen && currentVersion > 1 && (
        <div
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            background: "rgba(255,213,0,.04)",
            border: "1px solid rgba(255,213,0,.18)",
            fontSize: 11,
            color: "var(--ql-fg-muted)",
            fontFamily: "var(--ql-font-mono)",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <div style={{ color: "var(--ql-yellow)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4 }}>
            version history
          </div>
          <div style={{ color: "var(--ql-fg)" }}>
            v{currentVersion} · current · generated {timeAgo(draft.generatedAt)}
          </div>
          {versions === null ? (
            <div>Loading…</div>
          ) : versions.length === 0 ? (
            <div>(No prior versions on disk.)</div>
          ) : (
            versions.map((v) => (
              <div key={v.version} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                <img
                  src={betaPostVersionCoverUrl(draft.id, v.version)}
                  alt=""
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                  style={{ width: 30, height: 53, objectFit: "cover", borderRadius: 5, background: "rgba(255,255,255,.04)", flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "var(--ql-fg)" }}>v{v.version} · {timeAgo(v.generatedAt)}</div>
                  {v.quote && (
                    <div style={{ color: "var(--ql-fg-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {v.quote}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { void handleRestore(v.version); }}
                  disabled={restoring !== null}
                  title={`Roll back to v${v.version} — your current version is saved to history first`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 9px", borderRadius: 999, border: "1px solid rgba(255,213,0,.35)", background: "rgba(255,213,0,.10)", color: "var(--ql-yellow)", fontFamily: "inherit", fontSize: 10, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}
                >
                  <RotateCcw size={11} strokeWidth={2.2} />
                  {restoring === v.version ? "…" : "Restore"}
                </button>
              </div>
            ))
          )}
          <div style={{ marginTop: 6, opacity: 0.65, fontSize: 10 }}>
            Restore any version above — your current version is archived first, so rolling back is reversible.
          </div>
        </div>
      )}

      {restoreUndo && (
        <div style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: 24, zIndex: 60, display: "flex", alignItems: "center", gap: 14, padding: "12px 18px", borderRadius: 999, background: "rgba(16,18,27,.96)", border: "1px solid rgba(255,255,255,.12)", boxShadow: "0 8px 30px rgba(0,0,0,.5)" }}>
          <span style={{ fontSize: 13 }}>Rolled back</span>
          <button type="button" onClick={() => { const from = restoreUndo.from; setRestoreUndo(null); void handleRestore(from); }} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 999, border: "1px solid rgba(255,213,0,.4)", background: "rgba(255,213,0,.12)", color: "var(--ql-yellow)", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            <RotateCcw size={13} strokeWidth={2.2} /> Undo
          </button>
        </div>
      )}

      {/* Slide preview area */}
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "4 / 5",
          borderRadius: 14,
          overflow: "hidden",
          background: "#0A0908",
          border: "1px solid rgba(255,255,255,.06)",
        }}
      >
        {isFailed ? (
          // Failed-state panel — the text generation threw. Shows the REAL
          // reason (persisted on the draft) + points at Retry / With a note,
          // instead of the awaiting-image drop zone. Parity with the Legacy card.
          <div
            style={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              gap: 12, padding: 24, textAlign: "center",
              background: "radial-gradient(ellipse at center, rgba(248,113,113,.12) 0%, rgba(0,0,0,0) 65%)",
            }}
          >
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase",
              color: "rgba(248,113,113,.95)", fontFamily: "var(--ql-font-mono)",
            }}>
              Generation failed
            </div>
            <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", lineHeight: 1.55, maxWidth: 300 }}>
              {draft.errorMessage || "The text couldn't be generated. Retry, or add a note for more direction."}
            </div>
            <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)" }}>
              Retry below, or use “With a note” to give more direction.
            </div>
          </div>
        ) : isGeneratingText ? (
          // Stub-state loader. The draft is on disk with status=
          // generating-text and empty content while Claude writes the
          // hooks. Survives refresh because the row exists server-side.
          <div
            style={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              gap: 14, padding: 24,
              background: "radial-gradient(ellipse at center, rgba(255,213,0,.12) 0%, rgba(0,0,0,0) 65%)",
            }}
          >
            <Loader2 size={36} className="ql-spin" color="var(--ql-yellow)" />
            <div style={{
              fontSize: 11, fontWeight: 700,
              letterSpacing: ".14em", textTransform: "uppercase",
              color: "var(--ql-yellow)", fontFamily: "var(--ql-font-mono)",
              textAlign: "center",
            }}>
              Writing the post…
            </div>
            <div style={{
              fontSize: 11, color: "var(--ql-fg-muted)",
              textAlign: "center", lineHeight: 1.5,
              maxWidth: 260,
            }}>
              Claude is drafting the Arabic + English hooks and the
              image prompt (~10 s). You can leave the page — the card
              will fill in automatically.
            </div>
          </div>
        ) : hasImage && !showPromptPanel ? (
          <>
            {/* Peek-deck depth — the two upcoming slides ghosted behind the
                active one. Pure decoration; pointer-events: none so all
                interaction (slot selector, regenerate overlay, edit) lands
                on the foreground image and its existing controls. */}
            {[2, 1].map((off) => {
              const ghostIdx = (slideIdx + off) % SLOTS.length;
              return (
                <img
                  key={off}
                  src={`${betaSlotUrl(draft.id, SLOTS[ghostIdx])}?t=${cacheKey}`}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  style={{
                    position: "absolute",
                    inset: 14 + off * 6,
                    width: `calc(100% - ${(14 + off * 6) * 2}px)`,
                    height: `calc(100% - ${(14 + off * 6) * 2}px)`,
                    objectFit: "cover",
                    borderRadius: 10,
                    transform: `scale(${1 - off * 0.04}) translateY(${-off * 8}px)`,
                    opacity: 0.30 - off * 0.10,
                    filter: "blur(2px)",
                    zIndex: off === 1 ? 1 : 0,
                    pointerEvents: "none",
                  }}
                />
              );
            })}
            <img
              src={`${betaSlotUrl(draft.id, SLOTS[slideIdx])}?t=${cacheKey}`}
              alt={`Slide ${slideIdx + 1}`}
              loading="lazy"
              decoding="async"
              style={{
                position: "relative",
                zIndex: 2,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                boxShadow: "0 6px 20px rgba(0,0,0,.45)",
              }}
            />
            {/* Slide chevron navigation. The dots below also work; these
                are the discoverable hint that there's more than slide 1
                (the dots were getting missed). Loops at the ends so
                clicking right on slide 4/4 wraps to 1/4. */}
            <button
              type="button"
              aria-label="Previous slide"
              title="Previous slide"
              onClick={() => setSlideIdx((slideIdx - 1 + SLOTS.length) % SLOTS.length)}
              style={chevronStyle("left")}
            >
              <ChevronLeft size={20} strokeWidth={2.5} />
            </button>
            <button
              type="button"
              aria-label="Next slide"
              title="Next slide"
              onClick={() => setSlideIdx((slideIdx + 1) % SLOTS.length)}
              style={chevronStyle("right")}
            >
              <ChevronRight size={20} strokeWidth={2.5} />
            </button>
            {/* Slide counter — explicit "1 / 4" so the operator can
                see at a glance how many slides exist. The footer
                already has "Nº526 · 1/4" but it's small and easy to
                miss; this one is part of the slide viewer. */}
            <div
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 4,
                padding: "4px 10px",
                background: "rgba(0,0,0,.55)",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "var(--ql-font-mono)",
                borderRadius: 999,
                pointerEvents: "none",
                letterSpacing: ".06em",
                backdropFilter: "blur(4px)",
              }}
            >
              {slideIdx + 1} / {SLOTS.length}
            </div>
            {(draft.status === "rendering" || busy === "regen" || busy === "saveEdit" || busy === "rerender") && (
              <div style={{
                position: "absolute", inset: 0,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                background: "rgba(10,9,8,.72)",
                backdropFilter: "blur(2px)",
                gap: 12,
                zIndex: 3,
              }}>
                <Loader2 size={32} className="ql-spin" color="var(--ql-yellow)" />
                <span style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  color: "var(--ql-yellow)",
                  fontFamily: "var(--ql-font-mono)",
                }}>
                  {busy === "regen"
                    ? "Regenerating text…"
                    : busy === "saveEdit"
                      ? "Saving & re-rendering…"
                      : "Re-rendering slides…"}
                </span>
              </div>
            )}
            {/* Slide selector pills */}
            <div
              style={{
                position: "absolute",
                bottom: 12,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: 6,
              }}
            >
              {SLOTS.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSlideIdx(i)}
                  title={SLOT_LABEL[s]}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: i === slideIdx ? "var(--ql-yellow)" : "rgba(255,255,255,.35)",
                    border: 0,
                    cursor: "pointer",
                    padding: 0,
                  }}
                />
              ))}
            </div>
          </>
        ) : draft.status === "rendering" ? (
          // No image on disk yet AND the server marked this draft as
          // rendering — it's mid-OpenAI-call. Show a dedicated overlay
          // so a refresh / page revisit doesn't leave the operator
          // staring at the Generate button (which they'd plausibly
          // click again, double-firing the API call).
          <div
            style={{
              position: "absolute", inset: 0,
              display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              gap: 14, padding: 24,
              background: "radial-gradient(ellipse at center, rgba(255,213,0,.12) 0%, rgba(0,0,0,0) 65%)",
            }}
          >
            <Loader2 size={36} className="ql-spin" color="var(--ql-yellow)" />
            <div style={{
              fontSize: 11, fontWeight: 700,
              letterSpacing: ".14em", textTransform: "uppercase",
              color: "var(--ql-yellow)", fontFamily: "var(--ql-font-mono)",
              textAlign: "center",
            }}>
              Generating image…
            </div>
            <div style={{
              fontSize: 11, color: "var(--ql-fg-muted)",
              textAlign: "center", lineHeight: 1.5,
              maxWidth: 260,
            }}>
              GPT Image 2 is rendering the background (~30 s).
              You can leave the page — the slides will compose
              automatically and the card will flip to Ready.
            </div>
          </div>
        ) : (
          <ImageDropZone
            promptHint={draft.content.promptHint}
            url={imageUrl}
            setUrl={setImageUrl}
            onAttach={() => void handlePasteUrl()}
            onCopyPrompt={() => void copyPromptHint()}
            onGenerate={handleOpenGenerateModal}
            onRegenPrompt={() => void handleRegenPrompt()}
            busy={busy === "attach"}
            generating={busy === "generate"}
            regenerating={busy === "regenPrompt"}
          />
        )}
      </div>

      {error && (
        <div style={{
          padding: 10, borderRadius: 10,
          background: "rgba(248,113,113,.06)",
          border: "1px solid rgba(248,113,113,.30)",
          color: "var(--ql-danger)", fontSize: 12,
        }}>{error}</div>
      )}

      {/* Outcome of the last background action — server-persisted, so it's
          still here after a refresh or a page switch (2026-08-19). Hidden
          while a new action runs; that action will write its own. */}
      {!error && !serverBusy && draft.notice && (
        <div
          style={{
            fontSize: 11,
            color: /failed|could not/i.test(draft.notice.message)
              ? "#FCA5A5"
              : "var(--ql-fg-muted)",
            lineHeight: 1.5,
          }}
        >
          {draft.notice.message}
        </div>
      )}

      {/* Action row — hidden while the stub is still being written.
          Nothing meaningful to act on until Claude returns content. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", opacity: isGeneratingText ? 0.35 : 1, pointerEvents: isGeneratingText ? "none" : "auto" }}>
        {hasImage && (
          <button
            type="button"
            onClick={() => void handleCopyImage()}
            disabled={anyBusy || copyState === "copying"}
            title={`Copy slide ${slideIdx + 1} PNG to clipboard`}
            style={btnGhost(copyState === "copied")}
          >
            {copyState === "copying" ? (
              <Loader2 size={13} className="ql-spin" />
            ) : copyState === "copied" ? (
              <Check size={13} strokeWidth={2.4} color="#7BD389" />
            ) : (
              <Copy size={13} strokeWidth={2} />
            )}
            {copyState === "copied" ? "Copied!" : copyState === "error" ? "Failed" : `Copy slide ${slideIdx + 1}`}
          </button>
        )}
        {/* Edit is a dead-end on a failed draft (its text preview is replaced by
            the failed panel, so edits would appear to vanish). Recovery is
            Retry / With a note. */}
        {!isFailed && (
          <button
            type="button"
            onClick={() => setEditOpen((v) => !v)}
            disabled={anyBusy}
            title="Edit text, vibe, or pin cover font sizes — saves and re-renders in one step"
            style={btnGhost(editOpen)}
          >
            {editOpen ? <X size={13} strokeWidth={2} /> : <Pencil size={13} strokeWidth={2} />}
            {editOpen ? "Close edit" : "Edit"}
          </button>
        )}
        <button
          type="button"
          onClick={() => void handleRegen()}
          disabled={anyBusy}
          title="Regenerate a fresh version (same theme/seed)"
          style={btnGhost(busy === "regen" && !regenNoteOpen)}
        >
          {busy === "regen" && !regenNoteOpen ? <Loader2 size={13} className="ql-spin" /> : <Sparkles size={13} strokeWidth={2} />}
          {isFailed ? "Retry" : "Regenerate text"}
        </button>
        <button
          type="button"
          onClick={() => setRegenNoteOpen((v) => !v)}
          disabled={anyBusy}
          title="Regenerate with a note — tell it what to change"
          style={btnGhost(regenNoteOpen)}
        >
          {regenNoteOpen ? <X size={13} strokeWidth={2} /> : <MessageSquarePlus size={13} strokeWidth={2} />}
          With a note
        </button>
        {lengthControl && draft.content && (
          <>
            <button
              type="button"
              onClick={() => void handleLength("shorter")}
              disabled={anyBusy || blockedLength.shorter !== undefined}
              title={
                blockedLength.shorter ??
                "Re-cut the quote about 15% shorter — same meaning, reflections untouched"
              }
              style={btnGhost(busy === "shorter" || serverBusy?.action === "quote-shorter")}
            >
              {busy === "shorter" || serverBusy?.action === "quote-shorter" ? <Loader2 size={13} className="ql-spin" /> : <Minus size={13} strokeWidth={2} />}
              Shorter
            </button>
            <button
              type="button"
              onClick={() => void handleLength("longer")}
              disabled={anyBusy || blockedLength.longer !== undefined}
              title={
                blockedLength.longer ??
                "Re-cut the quote about 15% longer — same meaning, reflections untouched"
              }
              style={btnGhost(busy === "longer" || serverBusy?.action === "quote-longer")}
            >
              {busy === "longer" || serverBusy?.action === "quote-longer" ? <Loader2 size={13} className="ql-spin" /> : <Plus size={13} strokeWidth={2} />}
              Longer
            </button>
          </>
        )}
        {hasImage && (
          <button
            type="button"
            onClick={() => setShowPromptPanel((v) => !v)}
            disabled={anyBusy || draft.status === "rendering"}
            title={
              showPromptPanel
                ? "Back to slide preview"
                : "Step back to the image prompt — same view as the awaiting-image state. From there you can Regenerate prompt, edit it, paste a Higgsfield URL, or click Generate with GPT Image to fire a fresh render. Keeps the Arabic + English text untouched."
            }
            style={btnGhost(showPromptPanel || busy === "generate")}
          >
            {busy === "generate" ? (
              <Loader2 size={13} className="ql-spin" />
            ) : showPromptPanel ? (
              <X size={13} strokeWidth={2} />
            ) : (
              <ImagePlus size={13} strokeWidth={2} />
            )}
            {showPromptPanel ? "Back to slides" : "Regenerate image"}
          </button>
        )}
        {hasImage && (
          <button
            type="button"
            onClick={() => void handleRerender()}
            disabled={anyBusy}
            style={btnGhost(busy === "rerender")}
          >
            {busy === "rerender" ? <Loader2 size={13} className="ql-spin" /> : <RefreshCw size={13} strokeWidth={2} />}
            Re-render slides
          </button>
        )}
        {/* A published draft can't be re-published (the route 409s) — it
            gets "Schedule again" below instead, so the card never offers a
            button that can only fail. */}
        {hasImage && !isPublished && (
          <button
            type="button"
            onClick={() => setPublishOpen(true)}
            disabled={anyBusy}
            title="Push these 4 slides to Buffer (Instagram, Twitter, Pinterest, LinkedIn)"
            style={{
              ...btnGhost(false),
              borderColor: "rgba(123, 211, 137, .35)",
              color: "#7BD389",
              background: "rgba(123, 211, 137, .08)",
            }}
          >
            <Send size={13} strokeWidth={2} />
            Publish to Buffer
          </button>
        )}
        {isPublished && onRecycled && (
          <button
            type="button"
            onClick={() => void handleRecycle()}
            disabled={anyBusy}
            title="Put this post back on the calendar: makes a fresh copy with the same slides and opens the scheduler. The original stays Published."
            style={{
              ...btnGhost(busy === "recycle"),
              borderColor: "rgba(123, 211, 137, .35)",
              color: "#7BD389",
              background: "rgba(123, 211, 137, .08)",
            }}
          >
            {busy === "recycle" ? <Loader2 size={13} className="ql-spin" /> : <Repeat size={13} strokeWidth={2} />}
            Schedule again
          </button>
        )}
        <button
          type="button"
          onClick={() => void handleDiscard()}
          disabled={anyBusy}
          style={{
            ...btnGhost(busy === "discard"),
            color: "var(--ql-danger)",
            borderColor: "rgba(248,113,113,.25)",
          }}
        >
          <Trash2 size={13} strokeWidth={2} />
          Discard
        </button>
      </div>

      {regenNoteOpen && !isGeneratingText && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
          <textarea
            value={regenNote}
            onChange={(e) => setRegenNote(e.target.value)}
            placeholder="What should change? e.g. “make the Arabic punchier”, “less abstract”, “keep the hook, redo the reflection”, “new image angle”."
            rows={2}
            disabled={anyBusy}
            style={{
              resize: "vertical",
              width: "100%",
              boxSizing: "border-box",
              padding: "9px 11px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,.12)",
              background: "rgba(0,0,0,.22)",
              color: "var(--ql-fg)",
              fontSize: 12.5,
              lineHeight: 1.45,
              fontFamily: "inherit",
            }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => void handleRegen(regenNote)}
              disabled={anyBusy || regenNote.trim().length === 0}
              style={{
                ...btnGhost(busy === "regen"),
                borderColor: "rgba(255,213,0,.30)",
                color: "var(--ql-yellow)",
                background: "rgba(255,213,0,.08)",
                opacity: busy !== null || regenNote.trim().length === 0 ? 0.5 : 1,
              }}
            >
              {busy === "regen" ? <Loader2 size={13} className="ql-spin" /> : <Sparkles size={13} strokeWidth={2} />}
              Regenerate with this note
            </button>
            <button
              type="button"
              onClick={() => { setRegenNote(""); setRegenNoteOpen(false); }}
              disabled={anyBusy}
              style={btnGhost(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

    </div>

    {editOpen && (
      <EditModal
        draft={draft}
        cacheKey={cacheKey}
        slideIdx={slideIdx}
        setSlideIdx={setSlideIdx}
        busy={busy}
        edit={edit}
        setEdit={setEdit}
        onSave={() => void handleSaveEdit()}
        onCancel={() => { setEdit(initialEdit); setEditOpen(false); }}
        arSize={draft.slideOverrides?.coverArFontSize ?? 0}
        enSize={draft.slideOverrides?.coverEnFontSize ?? 0}
        onNudgeAr={(d) => void handleNudgeSize("ar", d)}
        onNudgeEn={(d) => void handleNudgeSize("en", d)}
        onResetAr={() => void handleResetSize("ar")}
        onResetEn={() => void handleResetSize("en")}
      />
    )}

    {publishOpen && (
      <PublishModal
        draft={draft}
        cacheKey={cacheKey}
        onClose={() => setPublishOpen(false)}
        onPublished={() => { setPublishOpen(false); onChange(); }}
      />
    )}

    {genModalOpen && (
      <GenerateImageModal
        defaultSelectedIds={draft.usedInspirationIds ?? []}
        onCancel={() => setGenModalOpen(false)}
        onConfirm={(ids) => void handleGenerateWithRefs(ids)}
      />
    )}

    <ConfirmDialog
      open={confirmDiscardOpen}
      title={`Discard draft "${draft.theme}"?`}
      message="This moves the draft to the discarded bucket. The Arabic / English quote stays searchable in Library Quotes — you don't lose the words."
      confirmLabel="Discard draft"
      danger
      busy={busy === "discard"}
      onConfirm={() => void confirmDiscard()}
      onCancel={() => setConfirmDiscardOpen(false)}
    />
    </>
  );
}

type EditState = {
  quote: string;
  arabicQuote: string;
  arabic: string;
  english: string;
  promptHint: string;
  vibe: string;
  attribution: string;
};

function EditPanel({
  edit,
  setEdit,
  saving,
  arSize,
  enSize,
  onNudgeAr,
  onNudgeEn,
  onResetAr,
  onResetEn,
}: {
  edit: EditState;
  setEdit: (next: EditState) => void;
  saving: boolean;
  arSize: number;
  enSize: number;
  onNudgeAr: (delta: number) => void;
  onNudgeEn: (delta: number) => void;
  onResetAr: () => void;
  onResetEn: () => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  const set = <K extends keyof EditState>(k: K, v: EditState[K]): void => setEdit({ ...edit, [k]: v });
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >

      <Field label="English hook (slide 2)">
        <textarea
          value={edit.quote}
          onChange={(e) => set("quote", e.target.value)}
          rows={2}
          style={txtStyle}
        />
      </Field>
      <Field label="Arabic hook (slide 1)">
        <textarea
          dir="rtl"
          value={edit.arabicQuote}
          onChange={(e) => set("arabicQuote", e.target.value)}
          rows={2}
          style={{ ...txtStyle, textAlign: "right", direction: "rtl", fontFamily: 'Aref Ruqaa, Amiri, system-ui' }}
        />
      </Field>
      <Field label="Arabic reflection (slide 3)">
        <textarea
          dir="rtl"
          value={edit.arabic}
          onChange={(e) => set("arabic", e.target.value)}
          rows={5}
          style={{ ...txtStyle, textAlign: "right", direction: "rtl", fontFamily: 'Aref Ruqaa, Amiri, system-ui' }}
        />
      </Field>
      <Field label="English reflection (slide 4)">
        <textarea
          value={edit.english}
          onChange={(e) => set("english", e.target.value)}
          rows={5}
          style={txtStyle}
        />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
        <Field label="Vibe">
          <input
            value={edit.vibe}
            onChange={(e) => set("vibe", e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Attribution (optional)">
          <input
            value={edit.attribution}
            onChange={(e) => set("attribution", e.target.value)}
            style={inputStyle}
          />
        </Field>
      </div>

      <Field label="Higgsfield prompt (image gen)">
        <textarea
          value={edit.promptHint}
          onChange={(e) => set("promptHint", e.target.value)}
          rows={4}
          style={txtStyle}
        />
      </Field>

      <div style={{
        marginTop: 6, paddingTop: 12,
        borderTop: "1px dashed rgba(255,255,255,.10)",
        display: "flex", flexDirection: "column", gap: 10,
      }}>
        <div style={{
          fontSize: 10, letterSpacing: ".10em", textTransform: "uppercase",
          color: "var(--ql-fg-subtle)", fontWeight: 700,
        }}>
          Cover font size — each click nudges by one small step. Saves &amp; re-renders instantly.
        </div>
        <SizeStepper
          label="Slide 1 · Arabic"
          size={arSize}
          onSmaller={() => onNudgeAr(-4)}
          onBigger={() => onNudgeAr(+4)}
          onReset={onResetAr}
          disabled={saving}
        />
        <SizeStepper
          label="Slide 2 · English"
          size={enSize}
          onSmaller={() => onNudgeEn(-4)}
          onBigger={() => onNudgeEn(+4)}
          onReset={onResetEn}
          disabled={saving}
        />
      </div>

    </div>
  );
}

function SizeStepper({
  label,
  size,
  onSmaller,
  onBigger,
  onReset,
  disabled,
}: {
  label: string;
  /** Currently pinned size in px (0 means auto). */
  size: number;
  onSmaller: () => void;
  onBigger: () => void;
  onReset: () => void;
  disabled: boolean;
}): React.JSX.Element {
  const isAuto = size === 0;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
    }}>
      <span style={{
        flex: "0 0 130px",
        fontSize: 12, fontWeight: 600, color: "var(--ql-fg)",
      }}>{label}</span>
      <button
        type="button"
        onClick={onSmaller}
        disabled={disabled}
        title="Slightly smaller"
        style={stepBtn(disabled)}
      >
        <Minus size={14} strokeWidth={2.4} />
        Smaller
      </button>
      <span style={{
        minWidth: 96, padding: "6px 10px",
        textAlign: "center",
        borderRadius: 999,
        border: `1px solid ${isAuto ? "rgba(255,255,255,.12)" : "rgba(255,213,0,.36)"}`,
        background: isAuto ? "rgba(255,255,255,.03)" : "rgba(255,213,0,.10)",
        color: isAuto ? "var(--ql-fg-muted)" : "var(--ql-yellow)",
        fontSize: 11, fontWeight: 700,
        fontFamily: "var(--ql-font-mono)",
        letterSpacing: ".06em",
      }}>
        {isAuto ? "AUTO" : `${size} px`}
      </span>
      <button
        type="button"
        onClick={onBigger}
        disabled={disabled}
        title="Slightly bigger"
        style={stepBtn(disabled)}
      >
        <Plus size={14} strokeWidth={2.4} />
        Bigger
      </button>
      {!isAuto && (
        <button
          type="button"
          onClick={onReset}
          disabled={disabled}
          title="Reset to auto-fit"
          style={{ ...stepBtn(disabled), opacity: 0.85 }}
        >
          <RotateCcw size={12} strokeWidth={2} />
          Auto
        </button>
      )}
    </div>
  );
}

function stepBtn(disabled: boolean): React.CSSProperties {
  return {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "6px 10px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,.10)",
    background: "rgba(255,255,255,.04)",
    color: "var(--ql-fg)",
    fontSize: 11, fontWeight: 600,
    cursor: disabled ? "wait" : "pointer",
    fontFamily: "inherit",
    opacity: disabled ? 0.5 : 1,
  };
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{
        fontSize: 10, letterSpacing: ".10em", textTransform: "uppercase",
        color: "var(--ql-fg-subtle)", fontWeight: 700,
      }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,.10)",
  background: "rgba(255,255,255,.04)",
  color: "var(--ql-fg)",
  fontSize: 12,
  fontFamily: "var(--ql-font-mono)",
  outline: "none",
  width: "100%",
};

const txtStyle: React.CSSProperties = {
  ...inputStyle,
  resize: "vertical",
  lineHeight: 1.5,
};

function ImageDropZone({
  promptHint,
  url,
  setUrl,
  onAttach,
  onCopyPrompt,
  onGenerate,
  onRegenPrompt,
  busy,
  generating,
  regenerating,
}: {
  promptHint: string;
  url: string;
  setUrl: (s: string) => void;
  onAttach: () => void;
  onCopyPrompt: () => void;
  onGenerate: () => void;
  onRegenPrompt: () => void;
  busy: boolean;
  generating: boolean;
  regenerating: boolean;
}): React.JSX.Element {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: 24,
        gap: 14,
        background:
          "radial-gradient(ellipse at center, rgba(255,213,0,.08) 0%, rgba(0,0,0,0) 60%)",
      }}
    >
      <div style={{
        fontSize: 11, color: "var(--ql-yellow)", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: ".08em",
      }}>
        Higgsfield prompt
      </div>
      <div
        style={{
          fontSize: 13,
          lineHeight: 1.5,
          color: "rgba(255,255,255,.85)",
          fontFamily: "var(--ql-font-mono)",
          maxHeight: 130,
          overflow: "auto",
        }}
      >
        {promptHint}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <button
          type="button"
          onClick={onGenerate}
          disabled={busy || generating}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 8,
            border: "1px solid rgba(255,213,0,.50)",
            background: "rgba(255,213,0,.14)",
            color: "var(--ql-yellow)",
            fontSize: 11,
            fontWeight: 700,
            cursor: generating ? "wait" : "pointer",
            fontFamily: "inherit",
            opacity: busy || generating ? 0.65 : 1,
          }}
        >
          {generating ? <Loader2 size={12} className="ql-spin" /> : <Sparkles size={12} strokeWidth={2} />}
          {generating ? "Generating…" : "Generate with GPT Image"}
        </button>
        <button
          type="button"
          onClick={onCopyPrompt}
          disabled={generating || regenerating}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 8,
            border: "1px solid rgba(255,213,0,.30)",
            background: "rgba(255,213,0,.10)",
            color: "var(--ql-yellow)",
            fontSize: 11,
            fontWeight: 700,
            cursor: generating || regenerating ? "not-allowed" : "pointer",
            fontFamily: "inherit",
            opacity: generating || regenerating ? 0.6 : 1,
          }}
        >
          <ClipboardCopy size={12} strokeWidth={2} />
          Copy prompt
        </button>
        <button
          type="button"
          onClick={onRegenPrompt}
          disabled={busy || generating || regenerating}
          title="Generate a fresh image prompt (keeps the quote and reflections). Use this when GPT Image rejects the current prompt or you want a different visual angle."
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,.12)",
            background: "rgba(255,255,255,.03)",
            color: "var(--ql-fg-muted)",
            fontSize: 11,
            fontWeight: 700,
            cursor: regenerating ? "wait" : (busy || generating ? "not-allowed" : "pointer"),
            fontFamily: "inherit",
            opacity: busy || generating ? 0.6 : 1,
          }}
        >
          {regenerating ? (
            <RefreshCw size={12} className="ql-spin" strokeWidth={2} />
          ) : (
            <RefreshCw size={12} strokeWidth={2} />
          )}
          {regenerating ? "Regenerating…" : "Regenerate prompt"}
        </button>
      </div>
      <div style={{ height: 1, background: "rgba(255,255,255,.06)", margin: "8px 0" }} />
      <div style={{ fontSize: 11, color: "var(--ql-fg-muted)" }}>
        …or paste a Higgsfield URL / local file path:
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://… or /Users/macbook/…/image.png"
          style={{
            flex: 1,
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,.10)",
            background: "rgba(255,255,255,.04)",
            color: "var(--ql-fg)",
            fontSize: 12,
            fontFamily: "var(--ql-font-mono)",
            outline: "none",
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") onAttach();
          }}
        />
        <button
          type="button"
          onClick={onAttach}
          disabled={busy || generating || !url.trim()}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border: "1px solid rgba(255,213,0,.30)",
            background: "rgba(255,213,0,.15)",
            color: "var(--ql-yellow)",
            fontSize: 12,
            fontWeight: 700,
            cursor: busy ? "wait" : "pointer",
            fontFamily: "inherit",
            opacity: busy || generating || !url.trim() ? 0.5 : 1,
          }}
        >
          {busy ? <Loader2 size={12} className="ql-spin" /> : "Attach"}
        </button>
      </div>
    </div>
  );
}

function btnGhost(active: boolean): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,.10)",
    background: active ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.03)",
    color: "var(--ql-fg)",
    fontSize: 12,
    fontWeight: 600,
    cursor: active ? "wait" : "pointer",
    fontFamily: "inherit",
  };
}

function timeAgo(ts: number): string {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

/**
 * Full-screen edit modal. Renders OVER the card with a dimmed/blurred
 * backdrop. The slide preview lives on the left (always visible, no
 * scrolling required) and the editor form scrolls independently on
 * the right. Size nudges and Save reactively re-render the slide on
 * the left in-place via the same cacheKey machinery.
 *
 * Close paths: Escape key, click backdrop, Cancel button, Save button.
 * Save and Cancel sit in a sticky footer so they're always reachable
 * even on a long-scrolled form.
 */
function EditModal({
  draft,
  cacheKey,
  slideIdx,
  setSlideIdx,
  busy,
  edit,
  setEdit,
  onSave,
  onCancel,
  arSize,
  enSize,
  onNudgeAr,
  onNudgeEn,
  onResetAr,
  onResetEn,
}: {
  draft: BetaPostDraft;
  cacheKey: number;
  slideIdx: number;
  setSlideIdx: (n: number) => void;
  busy: CardBusy;
  edit: EditState;
  setEdit: (next: EditState) => void;
  onSave: () => void;
  onCancel: () => void;
  arSize: number;
  enSize: number;
  onNudgeAr: (delta: number) => void;
  onNudgeEn: (delta: number) => void;
  onResetAr: () => void;
  onResetEn: () => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  const saving = busy === "saveEdit";
  const hasImage = !!draft.imageFilename;
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,.72)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%", maxWidth: isMobile ? "calc(100vw - 24px)" : 1280, maxHeight: "92vh",
          background: "rgba(16,18,27,.96)",
          border: "1px solid rgba(255,255,255,.10)",
          borderRadius: 18,
          display: "grid",
          gridTemplateRows: "auto 1fr auto",
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(0,0,0,.55)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: isMobile ? "12px 14px" : "16px 22px",
          borderBottom: "1px solid rgba(255,255,255,.06)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 14,
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <div style={{
              fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase",
              color: "var(--ql-yellow)", fontWeight: 700,
            }}>
              Inline edit
            </div>
            <div style={{
              fontSize: 16, fontWeight: 700, color: "var(--ql-fg)",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{draft.theme}</div>
          </div>
          <button
            type="button"
            onClick={() => { if (!saving) onCancel(); }}
            disabled={saving}
            aria-label="Close edit"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32, borderRadius: 8,
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.04)",
              color: "var(--ql-fg)", cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {/* Body — 2 columns, scrolls independently */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(320px, 1fr) minmax(360px, 1fr)",
          gap: 0,
          overflow: isMobile ? "auto" : "hidden",
        }}>
          {/* Left: slide preview, always visible. The outer column is
              vertically centred so the 4:5 preview never feels cramped
              against the top of the modal even on shorter viewports.
              `object-fit: contain` guarantees the WHOLE slide is shown
              — letterboxing blends into the near-black backdrop. */}
          <div style={{
            padding: isMobile ? 14 : 22,
            borderRight: isMobile ? "none" : "1px solid rgba(255,255,255,.06)",
            borderBottom: isMobile ? "1px solid rgba(255,255,255,.06)" : "none",
            display: "flex", flexDirection: "column", gap: 12,
            alignItems: "center", justifyContent: "center",
            overflow: "auto",
            minHeight: 0,
          }}>
            <div style={{
              position: "relative",
              width: "100%",
              aspectRatio: "4 / 5",
              maxHeight: "calc(92vh - 220px)",
              maxWidth: "calc((92vh - 220px) * (4 / 5))",
              borderRadius: 14,
              overflow: "hidden",
              background: "#0A0908",
              border: "1px solid rgba(255,255,255,.06)",
            }}>
              {hasImage ? (
                <>
                  <img
                    src={`${betaSlotUrl(draft.id, SLOTS[slideIdx])}?t=${cacheKey}`}
                    alt={`Slide ${slideIdx + 1}`}
                    style={{
                      width: "100%", height: "100%",
                      objectFit: "contain",
                      background: "#0A0908",
                    }}
                  />
                  {(busy === "saveEdit" || busy === "rerender" || busy === "regen") && (
                    <div style={{
                      position: "absolute", inset: 0,
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center",
                      background: "rgba(10,9,8,.72)",
                      backdropFilter: "blur(2px)",
                      gap: 12,
                    }}>
                      <Loader2 size={32} className="ql-spin" color="var(--ql-yellow)" />
                      <span style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
                        textTransform: "uppercase", color: "var(--ql-yellow)",
                        fontFamily: "var(--ql-font-mono)",
                      }}>
                        {busy === "regen" ? "Regenerating text…"
                          : busy === "saveEdit" ? "Saving & re-rendering…"
                          : "Re-rendering slides…"}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div style={{
                  position: "absolute", inset: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: 24,
                  color: "var(--ql-fg-muted)", fontSize: 13, textAlign: "center",
                }}>
                  No image attached yet. Close this modal, paste a Higgsfield URL
                  on the card to render the slides — then come back to edit.
                </div>
              )}
            </div>
            {hasImage && (
              <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                {SLOTS.map((s, i) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSlideIdx(i)}
                    title={SLOT_LABEL[s]}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 999,
                      border: `1px solid ${i === slideIdx ? "rgba(255,213,0,.36)" : "rgba(255,255,255,.10)"}`,
                      background: i === slideIdx ? "rgba(255,213,0,.12)" : "rgba(255,255,255,.03)",
                      color: i === slideIdx ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                      fontSize: 10, fontWeight: 700,
                      letterSpacing: ".06em",
                      cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    {i + 1} · {SLOT_LABEL[s]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: scrollable form */}
          <div style={{
            padding: isMobile ? 14 : 22,
            overflowY: "auto",
            minHeight: 0,
          }}>
            <EditPanel
              edit={edit}
              setEdit={setEdit}
              saving={saving}
              arSize={arSize}
              enSize={enSize}
              onNudgeAr={onNudgeAr}
              onNudgeEn={onNudgeEn}
              onResetAr={onResetAr}
              onResetEn={onResetEn}
            />
          </div>
        </div>

        {/* Sticky footer */}
        <div style={{
          padding: isMobile ? "12px 14px" : "14px 22px",
          borderTop: "1px solid rgba(255,255,255,.06)",
          background: "rgba(10,9,8,.6)",
          display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center",
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}>
          {!isMobile && (
          <span style={{
            marginRight: "auto",
            fontSize: 10, letterSpacing: ".10em", textTransform: "uppercase",
            color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)",
          }}>
            Tip — size nudges save instantly. Hit Save to commit text edits.
          </span>
          )}
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 14px", borderRadius: 8,
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.03)",
              color: "var(--ql-fg)",
              fontSize: 12, fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 16px", borderRadius: 8,
              border: "1px solid rgba(255,213,0,.40)",
              background: "rgba(255,213,0,.18)",
              color: "var(--ql-yellow)",
              fontSize: 12, fontWeight: 700,
              cursor: saving ? "wait" : "pointer",
              fontFamily: "inherit",
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? <Loader2 size={13} className="ql-spin" /> : <Save size={13} strokeWidth={2} />}
            Save text &amp; re-render
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Publish-to-Buffer modal.
 *
 * Operator contract per the brief:
 *   - Instagram + Twitter: full 4-slide carousel
 *   - Pinterest: first slide only (Pinterest doesn't carousel)
 *   - Instagram uses Buffer's channel-level "Notification" mode so the
 *     operator gets a phone push and adds music manually before posting
 *
 * Caption defaults to the template at `caption-beta-template.txt` with
 * `{{topic}}` substituted with the draft's theme. Operator can edit
 * freely before hitting Publish.
 */
function defaultDueAt(): string {
  // Default to tomorrow at 09:30 local time — gives the operator time
  // to review and Buffer time to queue. Rounded to the next half hour.
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 30, 0, 0);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function PublishModal({
  draft,
  cacheKey,
  onClose,
  onPublished,
}: {
  draft: BetaPostDraft;
  cacheKey: number;
  onClose: () => void;
  onPublished: () => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  const [channels, setChannels] = useState<SchedulingChannel[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Channels the operator flipped to Buffer's notification mode (phone
  // push at dueAt, finish manually). Everything else direct-publishes.
  const [notifyIds, setNotifyIds] = useState<Set<string>>(new Set());
  const [caption, setCaption] = useState<string>("");
  const [dueAt, setDueAt] = useState<string>(defaultDueAt());
  const [publishNow, setPublishNow] = useState<boolean>(false);
  const [previewSlot, setPreviewSlot] = useState<number>(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<BetaPublishResult[] | null>(null);

  // Lock body scroll + ESC to close while modal is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, busy]);

  // Load caption template + channels in parallel on mount.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getBetaCaptionTemplate(),
      listSchedulingChannels(),
    ])
      .then(([tpl, chans]) => {
        if (cancelled) return;
        // Substitute {{topic}} with the draft's theme (capitalised first
        // letter so it reads as a title-cased phrase in the caption).
        const topic = draft.theme.charAt(0).toUpperCase() + draft.theme.slice(1);
        setCaption(tpl.replace(/\{\{topic\}\}/g, topic));
        setChannels(chans);
        // Pre-select all IG / Twitter / Pinterest / LinkedIn channels by
        // default — operator can toggle off any they want to skip.
        const pre = new Set<string>();
        for (const c of chans) {
          if (CAROUSEL_SERVICES.includes(c.service)) {
            pre.add(c.id);
          }
        }
        setSelected(pre);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => { cancelled = true; };
  }, [draft.theme]);

  const toggle = (id: string): void => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleNotify = (id: string): void => {
    setNotifyIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const handlePublish = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      // "Publish now" → schedule for ~60 s in the future. Buffer
      // rejects dueAt = exact-now ("dueAt must be in the future")
      // because by the time the request lands their clock has moved
      // past ours. 60 s is well under Buffer's typical queue-tick
      // (~1 min) so this still publishes essentially immediately.
      const iso = publishNow
        ? new Date(Date.now() + 60_000).toISOString()
        : new Date(dueAt).toISOString();
      const scheduling: Record<string, "notification"> = {};
      for (const id of notifyIds) if (selected.has(id)) scheduling[id] = "notification";
      const res = await publishBetaDraft(draft.id, {
        caption,
        dueAt: iso,
        channelIds: Array.from(selected),
        scheduling,
      });
      setResults(res.results);
      // If everything succeeded, close & refresh after a short pulse.
      // When any channel failed, leave the modal open so the operator
      // can read the per-channel error and retry.
      const allOk = res.results.length > 0 && res.results.every((r) => r.postId);
      if (allOk) {
        setTimeout(onPublished, 1200);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Group channels by service for the toggle row layout.
  const byService = (svc: string): SchedulingChannel[] =>
    (channels ?? []).filter((c) => c.service === svc);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,.72)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{
        width: "100%", maxWidth: isMobile ? "calc(100vw - 24px)" : 980, maxHeight: "92vh",
        background: "rgba(16,18,27,.96)",
        border: "1px solid rgba(255,255,255,.10)",
        borderRadius: 18,
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
        overflow: "hidden",
        boxShadow: "0 24px 80px rgba(0,0,0,.55)",
      }}>
        {/* Header */}
        <div style={{
          padding: isMobile ? "12px 14px" : "16px 22px",
          borderBottom: "1px solid rgba(255,255,255,.06)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 14,
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <div style={{
              fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase",
              color: "#7BD389", fontWeight: 700,
            }}>
              Publish to Buffer
            </div>
            <div style={{
              fontSize: 16, fontWeight: 700, color: "var(--ql-fg)",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>{draft.theme}</div>
          </div>
          <button
            type="button"
            onClick={() => { if (!busy) onClose(); }}
            disabled={busy}
            aria-label="Close publish"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32, borderRadius: 8,
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.04)",
              color: "var(--ql-fg)", cursor: busy ? "not-allowed" : "pointer",
            }}
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {/* Body: 2 columns — left = first-slide preview, right = form */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(240px, 1fr) minmax(360px, 2fr)",
          overflow: isMobile ? "auto" : "hidden",
        }}>
          <div style={{
            padding: isMobile ? 14 : 22,
            borderRight: isMobile ? "none" : "1px solid rgba(255,255,255,.06)",
            borderBottom: isMobile ? "1px solid rgba(255,255,255,.06)" : "none",
            display: "flex", flexDirection: "column", gap: 10,
            alignItems: "center", justifyContent: "flex-start",
            overflow: "auto",
          }}>
            <img
              src={`${betaSlotUrl(draft.id, SLOTS[previewSlot])}?t=${cacheKey}`}
              alt={`Slide ${previewSlot + 1} preview`}
              style={{
                width: "100%", maxWidth: 320,
                aspectRatio: "4 / 5", objectFit: "contain",
                borderRadius: 14,
                background: "#0A0908",
                border: "1px solid rgba(255,255,255,.06)",
              }}
            />
            {/* Slot pills — same UX as the card preview, just smaller. */}
            <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
              {SLOTS.map((_, i) => {
                const active = i === previewSlot;
                return (
                  <button
                    type="button"
                    key={i}
                    onClick={() => setPreviewSlot(i)}
                    style={{
                      width: 24, height: 22, borderRadius: 6,
                      border: `1px solid ${active ? "rgba(123,211,137,.50)" : "rgba(255,255,255,.10)"}`,
                      background: active ? "rgba(123,211,137,.16)" : "rgba(255,255,255,.03)",
                      color: active ? "#7BD389" : "var(--ql-fg-subtle)",
                      fontSize: 10, fontWeight: 700, fontFamily: "var(--ql-font-mono)",
                      cursor: "pointer",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                    }}
                    aria-label={`Show slide ${i + 1}`}
                  >{i + 1}</button>
                );
              })}
            </div>
            <div style={{
              fontSize: 10, letterSpacing: ".10em", textTransform: "uppercase",
              color: "var(--ql-fg-subtle)", fontWeight: 700,
            }}>
              Slide {previewSlot + 1} of 4
            </div>
            <div style={{
              fontSize: 11, color: "var(--ql-fg-muted)", textAlign: "center", lineHeight: 1.5,
            }}>
              Instagram, Twitter &amp; LinkedIn receive all 4 slides as a
              carousel. Pinterest receives only slide&nbsp;1.
            </div>
          </div>

          <div style={{ padding: isMobile ? 14 : 22, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Channel toggles */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{
                fontSize: 10, letterSpacing: ".10em", textTransform: "uppercase",
                color: "var(--ql-fg-subtle)", fontWeight: 700,
              }}>
                Destinations
              </div>
              {channels === null ? (
                <div style={{ color: "var(--ql-fg-muted)", fontSize: 12 }}>
                  Loading channels…
                </div>
              ) : (
                CAROUSEL_SERVICES.map((svc) => {
                  const list = byService(svc);
                  if (list.length === 0) return <DestinationEmpty key={svc} service={svc} />;
                  const meta = svc === "pinterest" ? "slide 1 only" : "all 4 slides";
                  return list.map((c) => (
                    <DestinationToggle
                      key={c.id}
                      service={c.service}
                      name={c.name}
                      meta={meta}
                      selected={selected.has(c.id)}
                      onToggle={() => toggle(c.id)}
                      disabled={busy}
                      notify={notifyIds.has(c.id)}
                      onNotifyToggle={() => toggleNotify(c.id)}
                    />
                  ));
                })
              )}
            </div>

            {/* Schedule time */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{
                fontSize: 10, letterSpacing: ".10em", textTransform: "uppercase",
                color: "var(--ql-fg-subtle)", fontWeight: 700,
              }}>When</span>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setPublishNow(false)}
                  disabled={busy}
                  style={{
                    flex: 1,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: `1px solid ${!publishNow ? "rgba(123,211,137,.40)" : "rgba(255,255,255,.10)"}`,
                    background: !publishNow ? "rgba(123,211,137,.10)" : "rgba(255,255,255,.03)",
                    color: !publishNow ? "#7BD389" : "var(--ql-fg-muted)",
                    fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
                    textTransform: "uppercase", cursor: busy ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                  }}
                >Schedule</button>
                <button
                  type="button"
                  onClick={() => setPublishNow(true)}
                  disabled={busy}
                  style={{
                    flex: 1,
                    padding: "8px 10px",
                    borderRadius: 8,
                    border: `1px solid ${publishNow ? "rgba(123,211,137,.40)" : "rgba(255,255,255,.10)"}`,
                    background: publishNow ? "rgba(123,211,137,.10)" : "rgba(255,255,255,.03)",
                    color: publishNow ? "#7BD389" : "var(--ql-fg-muted)",
                    fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
                    textTransform: "uppercase", cursor: busy ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                  }}
                >Publish now</button>
              </div>
              {!publishNow ? (
                <BrandDateTimePicker
                  value={dueAt}
                  onChange={setDueAt}
                  disabled={busy}
                />
              ) : (
                <div style={{
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px dashed rgba(123,211,137,.30)",
                  background: "rgba(123,211,137,.04)",
                  color: "var(--ql-fg-muted)",
                  fontSize: 11,
                  fontFamily: "var(--ql-font-mono)",
                  lineHeight: 1.5,
                }}>
                  Schedules for ~1 minute from now (Buffer rejects exact-now).
                  Publishes on the next queue tick — essentially immediate.
                </div>
              )}
            </div>

            {/* Caption */}
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{
                fontSize: 10, letterSpacing: ".10em", textTransform: "uppercase",
                color: "var(--ql-fg-subtle)", fontWeight: 700,
              }}>Caption (same across all selected channels)</span>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={10}
                disabled={busy}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,.10)",
                  background: "rgba(255,255,255,.04)",
                  color: "var(--ql-fg)",
                  fontSize: 13,
                  lineHeight: 1.6,
                  fontFamily: "var(--ql-font-mono)",
                  outline: "none",
                  resize: "vertical",
                  minHeight: 200,
                }}
              />
            </label>

            {/* Result summary */}
            {results && (
              <div style={{
                display: "flex", flexDirection: "column", gap: 4,
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(123,211,137,.06)",
                border: "1px solid rgba(123,211,137,.20)",
                fontSize: 11, color: "var(--ql-fg-muted)",
                fontFamily: "var(--ql-font-mono)",
              }}>
                <div style={{ color: "#7BD389", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>
                  Publish result
                </div>
                {results.map((r) => (
                  <div key={r.channelId} style={{ color: r.error ? "var(--ql-danger)" : "var(--ql-fg)" }}>
                    {r.service} · {r.channelName ?? r.channelId} —{" "}
                    {r.error ? `FAILED: ${r.error}` : `OK (${r.postId?.slice(0, 8)}…)`}
                  </div>
                ))}
              </div>
            )}

            {error && (
              <div style={{
                padding: "10px 12px",
                borderRadius: 10,
                background: "rgba(248,113,113,.08)",
                border: "1px solid rgba(248,113,113,.30)",
                color: "var(--ql-danger)",
                fontSize: 12,
              }}>{error}</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: isMobile ? "12px 14px" : "14px 22px",
          borderTop: "1px solid rgba(255,255,255,.06)",
          background: "rgba(10,9,8,.6)",
          display: "flex", gap: 10, justifyContent: "flex-end", alignItems: "center",
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}>
          <span style={{
            marginRight: "auto",
            fontSize: 10, letterSpacing: ".10em", textTransform: "uppercase",
            color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)",
          }}>
            {selected.size > 0 ? `${selected.size} channel${selected.size > 1 ? "s" : ""} selected` : "Pick at least one channel"}
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 14px", borderRadius: 8,
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.03)",
              color: "var(--ql-fg)",
              fontSize: 12, fontWeight: 600,
              cursor: busy ? "not-allowed" : "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handlePublish()}
            disabled={busy || selected.size === 0 || !caption.trim() || (!publishNow && !dueAt)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "8px 16px", borderRadius: 8,
              border: "1px solid rgba(123,211,137,.40)",
              background: "rgba(123,211,137,.18)",
              color: "#7BD389",
              fontSize: 12, fontWeight: 700,
              cursor: busy ? "wait" : "pointer",
              fontFamily: "inherit",
              opacity: busy || selected.size === 0 || !caption.trim() || (!publishNow && !dueAt) ? 0.55 : 1,
            }}
          >
            {busy ? <Loader2 size={13} className="ql-spin" /> : <Send size={13} strokeWidth={2} />}
            {busy
              ? (publishNow ? "Posting now…" : "Scheduling…")
              : (() => {
                  const n = selected.size;
                  const label = n === 1 ? "1 channel" : `${n} channels`;
                  if (n === 0) return "Pick a channel";
                  return publishNow ? `Post now to ${label}` : `Schedule to ${label}`;
                })()}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Generate-image picker modal. Opens when the operator clicks
 * "Generate with GPT Image" — they choose up to 4 inspiration tiles
 * to pass as visual references to gpt-image-2, or skip references for
 * a text-only generation. Pre-selects whatever was used last time
 * (draft.usedInspirationIds) so iterative regenerations keep
 * continuity unless the operator changes the picks.
 */
function GenerateImageModal({
  defaultSelectedIds,
  onCancel,
  onConfirm,
}: {
  defaultSelectedIds: string[];
  onCancel: () => void;
  onConfirm: (ids: string[]) => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  const [entries, setEntries] = useState<InspirationEntry[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set(defaultSelectedIds));
  const [error, setError] = useState<string | null>(null);
  const MAX = 4;

  // Mount-only: load entries and apply the smart default ONCE. The
  // parent recreates `defaultSelectedIds` as a fresh `[]` array on
  // every render, so depending on it here re-ran the effect after
  // every toggle — which re-applied the top-3 default and undid the
  // operator's untick. Use a ref to capture the initial value and
  // depend on nothing so the effect runs exactly once per mount.
  const initialDefaultsRef = useRef(defaultSelectedIds);
  useEffect(() => {
    let cancelled = false;
    listInspiration()
      .then((es) => {
        if (cancelled) return;
        // Filter out source="generated" tiles by default. Feeding our
        // own shipped output back into the next round of image-gen
        // causes brand-aesthetic convergence — every new post starts
        // resembling the last one. The Inspiration page surfaces these
        // tiles for archival/review; the gen modal stays moodboard-only.
        // Legacy rows (no source field) default to "external" via the
        // backend's entrySource() helper, so they remain visible here.
        const externalOnly = es.filter((e) => e.source !== "generated");
        setEntries(externalOnly);
        // First-time defaults: pick 3 RANDOM tiles from the moodboard
        // (not the first 3) so the operator gets visual variety on
        // every generate. Pre-selecting always-the-top-3 caused the
        // post to lean on the same recent uploads over and over —
        // exactly the repetition we fight against on the prompt side.
        // Fisher-Yates partial shuffle: O(n) and unbiased.
        if (initialDefaultsRef.current.length === 0 && externalOnly.length > 0) {
          const N = Math.min(3, externalOnly.length);
          const pool = [...externalOnly];
          const picked: string[] = [];
          for (let i = 0; i < N; i++) {
            const j = i + Math.floor(Math.random() * (pool.length - i));
            [pool[i], pool[j]] = [pool[j]!, pool[i]!];
            picked.push(pool[i]!.id);
          }
          setSelected(new Set(picked));
        }
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onCancel]);

  const toggle = (id: string): void => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else if (n.size < MAX) n.add(id);
      return n;
    });
  };

  const ids = Array.from(selected);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,.72)",
        backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{
        width: "100%", maxWidth: isMobile ? "calc(100vw - 24px)" : 820, maxHeight: "92vh",
        background: "rgba(16,18,27,.96)",
        border: "1px solid rgba(255,255,255,.10)",
        borderRadius: 18,
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
        overflow: "hidden",
        boxShadow: "0 24px 80px rgba(0,0,0,.55)",
      }}>
        <div style={{
          padding: "16px 22px",
          borderBottom: "1px solid rgba(255,255,255,.06)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{
              fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase",
              color: "#7BD389", fontWeight: 700,
            }}>
              Generate with GPT Image
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ql-fg)" }}>
              Pick up to {MAX} visual references (or skip)
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32, borderRadius: 8,
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.04)",
              color: "var(--ql-fg)", cursor: "pointer",
            }}
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        <div style={{ padding: 22, overflowY: "auto" }}>
          {error && (
            <div style={{
              marginBottom: 14, padding: "10px 12px",
              borderRadius: 10,
              background: "rgba(248,113,113,.08)",
              border: "1px solid rgba(248,113,113,.30)",
              color: "var(--ql-danger)", fontSize: 12,
            }}>{error}</div>
          )}
          {entries === null ? (
            <div style={{ color: "var(--ql-fg-muted)", padding: 40, textAlign: "center", fontSize: 13 }}>
              Loading inspirations…
            </div>
          ) : entries.length === 0 ? (
            <div style={{
              padding: "40px 20px",
              textAlign: "center",
              border: "1px dashed rgba(255,255,255,.08)",
              borderRadius: 12,
              color: "var(--ql-fg-muted)",
              fontSize: 13, lineHeight: 1.6,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ql-fg)", marginBottom: 8 }}>
                No inspirations in your moodboard yet
              </div>
              You can still generate without references — gpt-image-2 will
              compose from the prompt alone. Add tiles to the Inspiration
              tab anytime; they'll show up here next time.
            </div>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 10,
            }}>
              {entries.map((e) => {
                const on = selected.has(e.id);
                const atMax = !on && selected.size >= MAX;
                return (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => toggle(e.id)}
                    disabled={atMax}
                    title={atMax ? `Max ${MAX} references — untick one first` : (e.note ?? "")}
                    style={{
                      position: "relative",
                      padding: 0, border: 0, background: "transparent",
                      cursor: atMax ? "not-allowed" : "pointer",
                      opacity: atMax ? 0.4 : 1,
                      borderRadius: 10,
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={inspirationImageUrl(e.id)}
                      alt={e.note ?? "Reference"}
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        objectFit: "cover",
                        display: "block",
                        borderRadius: 10,
                        border: `2px solid ${on ? "#7BD389" : "rgba(255,255,255,.08)"}`,
                        boxShadow: on ? "0 0 0 2px rgba(123,211,137,.20)" : "none",
                        transition: "all .12s var(--ql-ease)",
                      }}
                      loading="lazy"
                    />
                    {on && (
                      <div style={{
                        position: "absolute", top: 6, right: 6,
                        width: 22, height: 22, borderRadius: 999,
                        background: "#7BD389",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#0A0908",
                      }}>
                        <Check size={13} strokeWidth={3} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{
          padding: isMobile ? "12px 14px" : "14px 22px",
          borderTop: "1px solid rgba(255,255,255,.06)",
          background: "rgba(10,9,8,.6)",
          display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center",
          flexWrap: isMobile ? "wrap" : "nowrap",
        }}>
          <span style={{
            fontSize: 10, letterSpacing: ".10em", textTransform: "uppercase",
            color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)",
          }}>
            {selected.size === 0
              ? "No references — text-only generation"
              : `${selected.size} of ${MAX} reference${selected.size === 1 ? "" : "s"} selected`}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: "8px 14px", borderRadius: 8,
                border: "1px solid rgba(255,255,255,.10)",
                background: "rgba(255,255,255,.03)",
                color: "var(--ql-fg)",
                fontSize: 12, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(ids)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "8px 16px", borderRadius: 8,
                border: "1px solid rgba(123,211,137,.40)",
                background: "rgba(123,211,137,.18)",
                color: "#7BD389",
                fontSize: 12, fontWeight: 700,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              <Sparkles size={13} strokeWidth={2} />
              {selected.size === 0 ? "Generate (text-only)" : `Generate with ${selected.size} reference${selected.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Absolute-positioned chevron button overlay for the slide viewer.
 *  Sits centred on the left/right edge of the slide image. Stays
 *  visible (not hover-only) so the operator immediately sees the
 *  carousel affordance — the previous dot-only design had a
 *  discoverability problem (operator reported on 2026-05-26). */
function chevronStyle(side: "left" | "right"): React.CSSProperties {
  return {
    position: "absolute",
    [side]: 8,
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 4,
    width: 36,
    height: 36,
    borderRadius: 999,
    border: 0,
    background: "rgba(0,0,0,.55)",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    backdropFilter: "blur(4px)",
    boxShadow: "0 2px 8px rgba(0,0,0,.4)",
    transition: "background .15s, transform .15s",
  };
}
